import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useLocation } from 'react-router-dom'
import { PageHeader } from '@/shared/ui/PageHeader'
import { Card } from '@/shared/ui/Card'
import { Spinner } from '@/shared/ui/Spinner'
import { EmptyState } from '@/shared/ui/EmptyState'
import { Pagination } from '@/shared/ui/Pagination'
import { useAutoReassignHistory } from '@/api/hooks'
import { cn } from '@/shared/utils'
import { History, ArrowRight, Calendar, AlertTriangle } from 'lucide-react'

const REASONS = ['rejected', 'timeout', 'student_choice', 'no_match', 'max_hops'] as const

function reasonCls(reason?: string) {
  if (reason === 'rejected')       return 'bg-red-50 text-red-600'
  if (reason === 'timeout')        return 'bg-amber-50 text-amber-600'
  if (reason === 'student_choice') return 'bg-blue-50 text-blue-600'
  if (reason === 'no_match')       return 'bg-purple-50 text-purple-600'
  if (reason === 'max_hops')       return 'bg-gray-200 text-gray-700'
  return 'bg-gray-100 text-gray-600'
}

function fmtDT(v?: string | null) {
  if (!v) return '—'
  const d = new Date(String(v).replace(' ', 'T'))
  if (isNaN(d.getTime())) return v
  return `${d.toLocaleDateString('en-GB').replace(/\//g, '-')} ${d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: true })}`
}

// Field names aren't pinned down precisely by the API doc, so every accessor
// below tries the most likely shapes rather than assuming one exact schema.
function historyFields(row: any) {
  const bookingId = row.booking_id ?? row.booking?.id ?? row.id
  const reason = row.reason
  const oldTrainerName = row.from_trainer?.name ?? row.old_trainer?.name ?? row.from_trainer_name ?? row.old_trainer_name
  const newTrainerName = row.to_trainer?.name ?? row.new_trainer?.name ?? row.to_trainer_name ?? row.new_trainer_name
  const toTrainerId = row.to_trainer_id ?? row.new_trainer_id ?? row.to_trainer?.id ?? row.new_trainer?.id
  const createdAt = row.created_at
  return { bookingId, reason, oldTrainerName, newTrainerName, toTrainerId, createdAt }
}

// The exact meaning of a row depends on reason + whether a new trainer landed —
// spelling that out here means the table reads as "what happened", not a raw code.
function outcomeOf(f: ReturnType<typeof historyFields>, t: ReturnType<typeof useTranslation>['t']) {
  const assignedName = f.newTrainerName || (f.toTrainerId ? `#${f.toTrainerId}` : '')
  if (f.reason === 'rejected' || f.reason === 'timeout') {
    return f.toTrainerId
      ? t('autoReassignHistory.outcome.autoAssigned', { name: assignedName })
      : t('autoReassignHistory.outcome.waitingForStudent')
  }
  if (f.reason === 'student_choice') return t('autoReassignHistory.outcome.studentPicked', { name: assignedName })
  if (f.reason === 'no_match')       return t('autoReassignHistory.outcome.noMatch')
  if (f.reason === 'max_hops')       return t('autoReassignHistory.outcome.maxHops')
  return '—'
}

export function AutoReassignHistoryPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const bookingBasePath = location.pathname.startsWith('/school') ? '/school/booking' : '/dashboard/booking'
  const [bookingId, setBookingId] = useState('')
  const [trainerId, setTrainerId] = useState('')
  const [reason, setReason] = useState('')
  const [page, setPage] = useState(1)
  const PER_PAGE = 20

  const { data, isLoading, isError } = useAutoReassignHistory({
    booking_id: bookingId ? Number(bookingId) : undefined,
    trainer_id: trainerId ? Number(trainerId) : undefined,
    reason: reason || undefined,
    page, limit: PER_PAGE,
  })
  const rows: any[] = data?.data ?? []
  const total = data?.total ?? rows.length
  const lastPage = Math.max(1, Math.ceil(total / PER_PAGE))

  return (
    <div className="space-y-5">
      <PageHeader
        title={t('autoReassignHistory.title')}
        subtitle={t('autoReassignHistory.subtitle')}
        breadcrumbs={[{ label: t('nav.dashboard'), to: bookingBasePath.startsWith('/school') ? '/school/overview' : '/dashboard' }, { label: t('autoReassignHistory.title') }]}
      />

      {/* ── Filters ── */}
      <div className="flex items-center gap-2 flex-wrap">
        <input value={bookingId} onChange={e => { setBookingId(e.target.value.replace(/\D/g, '')); setPage(1) }}
          placeholder={t('autoReassignHistory.bookingIdPlaceholder')} inputMode="numeric"
          className="h-10 w-40 px-3 text-sm border border-gray-200 rounded-xl bg-white text-gray-700 placeholder-gray-400 focus:outline-none focus:border-brand-400 focus:ring-2 focus:ring-brand-400/30"/>
        <input value={trainerId} onChange={e => { setTrainerId(e.target.value.replace(/\D/g, '')); setPage(1) }}
          placeholder={t('autoReassignHistory.trainerIdPlaceholder')} inputMode="numeric"
          className="h-10 w-40 px-3 text-sm border border-gray-200 rounded-xl bg-white text-gray-700 placeholder-gray-400 focus:outline-none focus:border-brand-400 focus:ring-2 focus:ring-brand-400/30"/>
        <select value={reason} onChange={e => { setReason(e.target.value); setPage(1) }}
          className="h-10 px-3 text-sm border border-gray-200 rounded-xl bg-white text-gray-600 focus:outline-none focus:border-brand-400 focus:ring-2 focus:ring-brand-400/30">
          <option value="">{t('autoReassignHistory.allReasons')}</option>
          {REASONS.map(r => <option key={r} value={r}>{t(`autoReassignHistory.reason.${r}`)}</option>)}
        </select>
      </div>

      <Card padding={false}>
        {isLoading ? (
          <div className="flex justify-center py-16"><Spinner className="w-8 h-8"/></div>
        ) : isError ? (
          <div className="text-center text-sm text-red-500 py-16 flex flex-col items-center gap-2">
            <AlertTriangle size={20}/>{t('autoReassignHistory.loadError')}
          </div>
        ) : rows.length === 0 ? (
          <EmptyState message={t('autoReassignHistory.emptyState')} icon={<History size={22}/>}/>
        ) : (
          <div className="overflow-x-auto">
            <table className="drivys-table w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th>{t('autoReassignHistory.colBooking')}</th>
                  <th>{t('autoReassignHistory.colTrainerChange')}</th>
                  <th>{t('autoReassignHistory.colReason')}</th>
                  <th>{t('autoReassignHistory.colOutcome')}</th>
                  <th>{t('autoReassignHistory.colDate')}</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row, i) => {
                  const f = historyFields(row)
                  return (
                    <tr key={row.id ?? `${f.bookingId}-${i}`}>
                      <td>
                        <button onClick={() => navigate(`${bookingBasePath}/${f.bookingId}`)} className="font-semibold text-brand-600 hover:underline">
                          #{f.bookingId}
                        </button>
                      </td>
                      <td className="whitespace-nowrap">
                        <div className="flex items-center gap-1.5 text-xs">
                          <span className="font-medium text-gray-600">{f.oldTrainerName || '—'}</span>
                          <ArrowRight size={11} className="text-gray-300 flex-shrink-0"/>
                          <span className={cn('font-medium', f.newTrainerName ? 'text-gray-800' : 'text-gray-400 italic')}>
                            {f.newTrainerName || t('autoReassignHistory.none')}
                          </span>
                        </div>
                      </td>
                      <td><span className={cn('px-2.5 py-0.5 rounded-full text-[12px] font-bold whitespace-nowrap', reasonCls(f.reason))}>{t(`autoReassignHistory.reason.${f.reason}`, String(f.reason ?? '—'))}</span></td>
                      <td className="text-gray-600 text-xs max-w-xs">{outcomeOf(f, t)}</td>
                      <td className="text-gray-600 whitespace-nowrap"><span className="flex items-center gap-1"><Calendar size={11} className="text-gray-400"/>{fmtDT(f.createdAt)}</span></td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}

        {total > PER_PAGE && (
          <Pagination page={page} lastPage={lastPage} total={total} perPage={PER_PAGE} onPageChange={setPage}/>
        )}
      </Card>
    </div>
  )
}
